/**
 * DuplicatePromoButton — one-click "clone this promotion" action.
 *
 * Same visual language as CouponHeroBlock's action buttons, so it drops cleanly
 * into the coupon hero's action row AND the automatic-rule summary. All logic
 * lives in useDuplicatePricingRule (which reuses the wizard's create path).
 */

import { CopyPlus } from "lucide-react";
import { useDuplicatePricingRule } from "./useDuplicatePricingRule";
import type { Lang } from "./pricingRuleDetailModel";

const pick = (lang: Lang, th: string, en: string) => (lang === "th" ? th : en);

export function DuplicatePromoButton({
  formData,
  lang,
}: {
  formData: Record<string, any>;
  lang: Lang;
}) {
  const { duplicate, isPending } = useDuplicatePricingRule(formData, lang);

  return (
    <button
      type="button"
      onClick={duplicate}
      disabled={isPending}
      title={pick(
        lang,
        "ทำสำเนาโปรโมชั่นนี้เป็นฉบับร่าง (ปิดใช้งานอยู่) แล้วปรับแก้",
        "Clone this promotion as a disabled draft, then tweak it",
      )}
      className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-[13px] font-medium text-gray-700 transition-colors hover:border-sky-300 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-200 dark:hover:border-sky-500/40 dark:hover:text-sky-400"
    >
      <CopyPlus className="h-3.5 w-3.5" />
      {isPending
        ? pick(lang, "กำลังทำสำเนา...", "Duplicating...")
        : pick(lang, "ทำสำเนา", "Duplicate")}
    </button>
  );
}
